import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import {
  getAdminPortfolios,
  createAdminPortfolio,
  updateAdminPortfolio,
  deleteAdminPortfolio,
} from "@/api/admin";
import AdminHeader from "@/components/AdminHeader";
import type { AdminPortfolio, CreatePortfolioRequest } from "@/types/api";

type FilterKey = "all" | "y" | "n";

const filterTabs: { key: FilterKey; label: string }[] = [
  { key: "all", label: "전체" },
  { key: "y", label: "활성" },
  { key: "n", label: "비활성" },
];

interface PortfolioForm extends CreatePortfolioRequest {
  activeYn: "y" | "n";
}

const emptyForm: PortfolioForm = {
  title: "",
  description: "",
  imageUrl: "",
  category: "",
  sortOrder: 0,
  activeYn: "y",
};

export default function AdminPortfoliosPage() {
  const [activeTab, setActiveTab] = useState<FilterKey>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdminPortfolio | null>(null);
  const [form, setForm] = useState<PortfolioForm>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const { data: portfolios, loading, refetch } = useFetch(getAdminPortfolios);

  const list: AdminPortfolio[] = Array.isArray(portfolios)
    ? portfolios
    : (portfolios as any)?.portfolios ?? (portfolios as any)?.content ?? [];
  const filteredList =
    activeTab === "all"
      ? list
      : list.filter((p) => p.activeYn === activeTab);

  const getFilterCount = (key: FilterKey) => {
    if (key === "all") return list.length;
    return list.filter((p) => p.activeYn === key).length;
  };

  const openCreateModal = () => {
    setEditingItem(null);
    setForm({ ...emptyForm });
    setModalOpen(true);
  };

  const openEditModal = (item: AdminPortfolio) => {
    setEditingItem(item);
    setForm({
      title: item.title || "",
      description: item.description || "",
      imageUrl: item.imageUrl,
      category: item.category || "",
      sortOrder: item.sortOrder,
      activeYn: item.activeYn,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async () => {
    if (!form.imageUrl.trim()) return;
    setSaving(true);
    try {
      const { activeYn, ...createData } = form;
      if (editingItem) {
        await updateAdminPortfolio(editingItem.portfolioIdx, {
          ...createData,
          activeYn,
        });
      } else {
        await createAdminPortfolio(createData);
      }
      closeModal();
      refetch();
    } catch (err) {
      console.error("포트폴리오 저장 실패:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (portfolioIdx: number) => {
    try {
      await deleteAdminPortfolio(portfolioIdx);
      setDeleteConfirm(null);
      refetch();
    } catch (err) {
      console.error("포트폴리오 삭제 실패:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AdminHeader />
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-slate-500">불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              포트폴리오 관리
            </h2>
            <p className="text-sm text-slate-600">
              웨딩 포트폴리오를 등록하고 관리하세요
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition-colors"
          >
            <i className="ri-add-line text-base"></i>
            포트폴리오 추가
          </button>
        </div>

        {/* Filter Tabs + Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="flex border-b border-slate-200">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap relative ${
                  activeTab === tab.key
                    ? "text-amber-600"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                <span>{tab.label}</span>
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.key
                      ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {getFilterCount(tab.key)}
                </span>
                {activeTab === tab.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-600"></div>
                )}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">
                    이미지
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">
                    카테고리
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">
                    설명
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 whitespace-nowrap">
                    정렬순서
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 whitespace-nowrap">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">
                    등록일
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-700 whitespace-nowrap">
                    관리
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-12 text-center text-sm text-slate-500"
                    >
                      <i className="ri-image-2-line text-4xl text-slate-300 mb-3 block"></i>
                      등록된 포트폴리오가 없습니다
                    </td>
                  </tr>
                ) : (
                  filteredList.map((item) => (
                    <tr
                      key={item.portfolioIdx}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        {item.imageUrl ? (
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className="w-16 h-20 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-16 h-20 rounded-lg bg-slate-100 flex items-center justify-center">
                            <i className="ri-image-line text-slate-400"></i>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900 whitespace-nowrap">
                        {item.title || "-"}
                      </td>
                      <td className="px-6 py-4">
                        {item.category ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {item.category}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate">
                        {item.description || "-"}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 text-center">
                        {item.sortOrder}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            item.activeYn === "y"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {item.activeYn === "y" ? "활성" : "비활성"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 whitespace-nowrap">
                        {new Date(item.createDt).toLocaleDateString("ko-KR")}
                      </td>
                      <td className="px-6 py-4 text-center whitespace-nowrap">
                        <button
                          onClick={() => openEditModal(item)}
                          className="text-sm text-amber-600 hover:text-amber-700 font-medium mr-3"
                        >
                          수정
                        </button>
                        <button
                          onClick={() =>
                            setDeleteConfirm(item.portfolioIdx)
                          }
                          className="text-sm text-red-500 hover:text-red-600 font-medium"
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600">전체 포트폴리오</span>
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                <i className="ri-image-2-line text-slate-600"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{list.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-emerald-600">활성</span>
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-emerald-600"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              {getFilterCount("y")}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-500">비활성</span>
              <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                <i className="ri-close-circle-line text-slate-400"></i>
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-500">
              {getFilterCount("n")}
            </p>
          </div>
        </div>
      </main>

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-900">
                {editingItem ? "포트폴리오 수정" : "포트폴리오 추가"}
              </h3>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
              >
                <i className="ri-close-line text-xl text-slate-500"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  이미지 URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.imageUrl}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, imageUrl: e.target.value }))
                  }
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
                {form.imageUrl && (
                  <img
                    src={form.imageUrl}
                    alt="미리보기"
                    className="mt-2 w-full h-40 object-cover rounded-lg border border-slate-200"
                  />
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  제목
                </label>
                <input
                  type="text"
                  value={form.title || ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                  placeholder="예: 봄날 웨딩 촬영"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  설명
                </label>
                <textarea
                  value={form.description || ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, description: e.target.value }))
                  }
                  rows={2}
                  placeholder="포트폴리오 설명"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    카테고리
                  </label>
                  <input
                    type="text"
                    value={form.category || ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, category: e.target.value }))
                    }
                    placeholder="예: 야외 정원"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    정렬 순서
                  </label>
                  <input
                    type="number"
                    value={form.sortOrder || 0}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        sortOrder: Number(e.target.value),
                      }))
                    }
                    min={0}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>
              </div>

              {/* Active (edit only) */}
              {editingItem && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    활성 상태
                  </label>
                  <select
                    value={form.activeYn}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        activeYn: e.target.value as "y" | "n",
                      }))
                    }
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
                  >
                    <option value="y">활성</option>
                    <option value="n">비활성</option>
                  </select>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={!form.imageUrl.trim() || saving}
                className="px-5 py-2.5 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? "저장 중..."
                  : editingItem
                    ? "수정"
                    : "등록"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Dialog */}
      {deleteConfirm !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-delete-bin-line text-xl text-red-500"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                포트폴리오 삭제
              </h3>
              <p className="text-sm text-slate-600">
                이 포트폴리오를 삭제하시겠습니까?
                <br />
                삭제된 항목은 비활성 처리됩니다.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
